import React, { useState } from 'react';
import environment from '../config/environment';
import './GrafanaMonitoring.css';

interface Dashboard {
  id: string;
  name: string;
  uid: string;
}

interface TimeRange {
  value: string;
  label: string;
}

const GrafanaMonitoring: React.FC = () => {
  const [selectedDashboard, setSelectedDashboard] = useState<string>('overview');
  const [timeRange, setTimeRange] = useState<string>('1h');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const grafanaBaseUrl = environment.GRAFANA_URL;
  const prometheusBaseUrl = environment.PROMETHEUS_URL;

  const dashboards: Dashboard[] = [
    { id: 'overview', name: 'API Overview', uid: 'vozyme-overview/vozyme-api-overview' },
  ];

  const timeRanges: TimeRange[] = [
    { value: '15m', label: 'Last 15 minutes' },
    { value: '1h', label: 'Last 1 hour' },
    { value: '3h', label: 'Last 3 hours' },
    { value: '6h', label: 'Last 6 hours' },
    { value: '12h', label: 'Last 12 hours' },
    { value: '24h', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
  ];

  const selectedDash = dashboards.find(d => d.id === selectedDashboard);

  // Build Grafana URL with kiosk mode for embedding
  const grafanaUrl = `${grafanaBaseUrl}/d/${selectedDash?.uid}?orgId=1&refresh=30s&from=now-${timeRange}&to=now&kiosk=tv`;

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const openInNewTab = (): void => {
    window.open(grafanaUrl.replace('&kiosk=tv', ''), '_blank');
  };

  return (
    <div className={`grafana-monitoring ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className="dashboard-header">
        <div className="header-content">
          <h2>
            <i className="fas fa-chart-area"></i>
            Grafana Monitoring
          </h2>
          <p className="last-updated">
            Real-time metrics and log visualization
          </p>
        </div>
        <div className="header-controls">
          <select
            value={selectedDashboard}
            onChange={(e) => setSelectedDashboard(e.target.value)}
            className="control-select"
          >
            {dashboards.map(dash => (
              <option key={dash.id} value={dash.id}>{dash.name}</option>
            ))}
          </select>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="control-select"
          >
            {timeRanges.map(range => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </select>
          <button
            className="control-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
          </button>
          <button
            className="control-btn"
            onClick={openInNewTab}
            title="Open in new tab"
          >
            <i className="fas fa-external-link-alt"></i>
          </button>
        </div>
      </div>

      <div className="grafana-container">
        <iframe
          src={grafanaUrl}
          title="Grafana Dashboard"
          className="grafana-iframe"
          frameBorder={0}
          allowFullScreen
        />
      </div>

      <div className="grafana-info">
        <div className="info-card">
          <i className="fas fa-info-circle"></i>
          <div>
            <strong>Grafana Access</strong>
            <p>
              Direct access: <a href={grafanaBaseUrl} target="_blank" rel="noopener noreferrer">
                {grafanaBaseUrl}
              </a>
            </p>
            <p>Login: admin / vozyme_grafana</p>
          </div>
        </div>
        <div className="info-card">
          <i className="fas fa-database"></i>
          <div>
            <strong>Data Sources</strong>
            <p>Prometheus: API metrics, request rates, latency</p>
            <p>Loki: Application logs, error tracking</p>
          </div>
        </div>
        <div className="info-card">
          <i className="fas fa-terminal"></i>
          <div>
            <strong>Quick Links</strong>
            <p>
              <a href={prometheusBaseUrl} target="_blank" rel="noopener noreferrer">
                Prometheus UI
              </a>
              {' | '}
              <a href={`${environment.API_BASE_URL}/metrics`} target="_blank" rel="noopener noreferrer">
                API Metrics
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrafanaMonitoring;
